import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { StrictMode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, Link, useNavigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
const API_BASE = process.env.REACT_APP_ID_API_BASE || process.env.REACT_APP_IDP_ORIGIN || '';
const FP_STORAGE_KEY = 'csrf_fp';
/** -------- cache-busting helpers -------- */
function stampParams() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    return { date: `${yyyy}-${mm}-${dd}`, h: hh };
}
function withStamp(to) {
    if (typeof to !== 'string')
        return to;
    const url = new URL(to, 'https://dummy.local'); // base для парсинга
    const { date, h } = stampParams();
    url.searchParams.set('date', date);
    url.searchParams.set('h', h);
    // возвращаем только путь + query + hash
    return `${url.pathname}${url.search}${url.hash}`;
}
/** Обертка над <Link>, автоматически добавляет ?date&h */
function LinkNoCache(props) {
    const nextTo = typeof props.to === 'string' ? withStamp(props.to) : props.to;
    return _jsx(Link, { ...props, to: nextTo });
}
/** -------- auth utils -------- */
function readAccessTokenFromSession() {
    try {
        return sessionStorage.getItem('access_token');
    }
    catch {
        return null;
    }
}
function readCookie(name) {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
}
function getRefreshFingerprint() {
    const cookieValue = readCookie('fp');
    if (cookieValue)
        return cookieValue;
    try {
        return localStorage.getItem(FP_STORAGE_KEY) || '';
    }
    catch {
        return '';
    }
}
function useAuth() {
    const [state, setState] = React.useState(() => {
        const token = readAccessTokenFromSession();
        if (token) {
            return { status: 'authed', token };
        }
        return { status: 'checking', token: null };
    });
    React.useEffect(() => {
        if (state.status !== 'checking')
            return;
        let cancelled = false;
        async function restoreSession() {
            try {
                const headers = {
                    'Cache-Control': 'no-store, no-cache, max-age=0',
                    'Pragma': 'no-cache',
                };
                const fingerprint = getRefreshFingerprint();
                if (fingerprint) {
                    headers['X-CSRF-Token'] = fingerprint;
                }
                const response = await fetch(`${API_BASE}/api/refresh`, {
                    method: 'POST',
                    credentials: 'include',
                    cache: 'no-store',
                    headers,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.access_token) {
                    try {
                        sessionStorage.removeItem('access_token');
                        sessionStorage.removeItem('refresh_token');
                    }
                    catch { }
                    if (!cancelled) {
                        setState({ status: 'guest', token: null });
                    }
                    return;
                }
                try {
                    sessionStorage.setItem('access_token', data.access_token);
                    if (data.refresh_token) {
                        sessionStorage.setItem('refresh_token', data.refresh_token);
                    }
                }
                catch { }
                if (!cancelled) {
                    setState({ status: 'authed', token: data.access_token });
                }
            }
            catch {
                if (!cancelled) {
                    setState({ status: 'guest', token: null });
                }
            }
        }
        void restoreSession();
        return () => {
            cancelled = true;
        };
    }, [state.status]);
    return {
        token: state.token,
        isAuthed: state.status === 'authed',
        isChecking: state.status === 'checking',
    };
}
function Protected({ children }) {
    const { isAuthed, isChecking } = useAuth();
    if (isChecking) {
        return _jsx("div", { style: { padding: 24 }, children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C \u0430\u0432\u0442\u043E\u0440\u0438\u0437\u0430\u0446\u0438\u044E..." });
    }
    if (!isAuthed)
        return _jsx(Navigate, { to: withStamp('/login'), replace: true });
    return children;
}
/** -------- pages -------- */
function Dashboard() {
    return (_jsxs("div", { style: { padding: 24 }, children: [_jsx("h1", { style: { margin: 0 }, children: "\u041B\u0438\u0447\u043D\u044B\u0439 \u043A\u0430\u0431\u0438\u043D\u0435\u0442" }), _jsx("p", { children: "\u0412\u044B \u0432\u043E\u0448\u043B\u0438. \u0417\u0434\u0435\u0441\u044C \u0431\u0443\u0434\u0435\u0442 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0435." }), _jsxs("div", { style: { display: 'flex', gap: 12, marginTop: 12 }, children: [_jsx(LinkNoCache, { to: "/profile", children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx(LogoutButton, {})] })] }));
}
function Profile() {
    return (_jsxs("div", { style: { padding: 24 }, children: [_jsx("h2", { children: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C" }), _jsx("p", { children: "\u0417\u0430\u0433\u043B\u0443\u0448\u043A\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u044B \u043F\u0440\u043E\u0444\u0438\u043B\u044F." })] }));
}
function LogoutButton() {
    const [busy, setBusy] = React.useState(false);
    const nav = useNavigate();
    async function logout() {
        setBusy(true);
        try {
            await fetch(`${API_BASE}/api/logout`, {
                method: 'POST',
                credentials: 'include',
                // максимальное отключение кеширования на всякий случай
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-store, no-cache, max-age=0',
                    'Pragma': 'no-cache',
                },
            });
        }
        catch { }
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        // мягкая навигация с меткой
        nav(withStamp('/login'), { replace: true });
    }
    return (_jsx("button", { onClick: logout, disabled: busy, children: busy ? 'Выходим...' : 'Выйти' }));
}
/** -------- app -------- */
export default function App() {
    return (_jsx(StrictMode, { children: _jsx(BrowserRouter, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(Protected, { children: _jsx(Dashboard, {}) }) }), _jsx(Route, { path: "/profile", element: _jsx(Protected, { children: _jsx(Profile, {}) }) }), _jsx(Route, { path: "/login", element: _jsx(LoginPage, {}) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: withStamp('/'), replace: true }) })] }) }) }));
}
